import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  cn,
} from '@parcel/ui';
import * as MenuPrimitive from '@radix-ui/react-dropdown-menu';
import { useQuery } from '@tanstack/react-query';
import { Link, useRouterState } from '@tanstack/react-router';
import { BookOpen, Laptop, LogOut, Moon, Settings, Sun, SunMoon } from 'lucide-react';
import { useAuth } from '../lib/auth.js';
import { type ThemePreference, useTheme } from '../lib/theme.js';
import {
  type UserProfile,
  getStructuredAuthMetadataProfileFallback,
  getUserProfile,
  getUserProfileDisplayLabel,
  getUserProfileNameSource,
} from '../lib/workspaces.js';
import { getWorkspaceScopedPath } from './app-sidebar-nav.js';
import { AuthenticatedImage } from './auth/authenticated-image.js';
import { Hint } from './hint.js';

export function getSidebarUserMenuLabel(userName: string, userEmail: string) {
  return `Open user menu for ${userName}, ${userEmail}`;
}

export function getSidebarUserName(
  profile: Pick<UserProfile, 'displayName' | 'firstName'> | null | undefined,
  email: string | null | undefined,
) {
  return getUserProfileDisplayLabel({
    displayName: profile?.displayName,
    firstName: profile?.firstName,
    email,
  });
}

function getUserInitials(name: string, email?: string) {
  const source = name === 'Signed-in user' ? email : name;
  if (!source) return 'U';

  const parts = source
    .replace(/@.*/, '')
    .split(/[\s._-]+/)
    .filter(Boolean);

  const [first = 'U', second] = parts;
  return (second ? `${first.charAt(0)}${second.charAt(0)}` : first.slice(0, 2)).toUpperCase();
}

/**
 * Profile menu in the app header (top-right), next to search.
 *
 * Same menu contents as the former sidebar footer — personal settings, theme,
 * log out — with an avatar-only trigger so it fits the slim header like Ramp.
 */
export function AppUserMenu() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { session, signOut } = useAuth();
  const { theme, setTheme } = useTheme();
  const profileQuery = useQuery({
    queryKey: ['me'],
    queryFn: getUserProfile,
  });

  const userEmail = session?.user.email ?? 'Authenticated';
  const userProfileName = getUserProfileNameSource(
    profileQuery.data,
    getStructuredAuthMetadataProfileFallback(session?.user.user_metadata),
  );
  const userName = getSidebarUserName(userProfileName, session?.user.email);
  const userInitials = getUserInitials(userName, session?.user.email);
  const avatarUrl = profileQuery.data?.avatarUrl ?? null;
  const settingsProfilePath = getWorkspaceScopedPath(pathname, '/settings/profile');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={getSidebarUserMenuLabel(userName, userEmail)}
          className="flex size-9 shrink-0 items-center justify-center rounded-lg outline-none ring-offset-background transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 data-[state=open]:bg-muted"
        >
          <span className="flex size-8 items-center justify-center overflow-hidden rounded-lg bg-muted text-xs font-medium text-foreground">
            {avatarUrl ? (
              <AuthenticatedImage
                src={avatarUrl}
                alt={userName}
                className="h-full w-full object-cover"
              />
            ) : (
              userInitials
            )}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" side="bottom" sideOffset={4} className="min-w-64">
        <DropdownMenuLabel className="font-normal">
          <div className="flex items-center gap-2">
            <div className="flex size-8 items-center justify-center overflow-hidden rounded-lg bg-muted text-xs font-medium text-foreground">
              {avatarUrl ? (
                <AuthenticatedImage
                  src={avatarUrl}
                  alt={userName}
                  className="h-full w-full object-cover"
                />
              ) : (
                userInitials
              )}
            </div>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-semibold">{userName}</span>
              <span className="truncate text-xs text-muted-foreground">{userEmail}</span>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <ThemeRow theme={theme} onThemeChange={setTheme} />
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to={settingsProfilePath}>
            <Settings />
            Settings
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a href={DOCS_URL} target="_blank" rel="noreferrer">
            <BookOpen />
            Docs
          </a>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => signOut()}>
          <LogOut />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const DOCS_URL = 'https://docs.parcelengineering.com';

const THEME_CHOICES: { value: ThemePreference; label: string; icon: typeof Sun }[] = [
  { value: 'system', label: 'System', icon: Laptop },
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
];

/** Theme as one row: system, light and dark side by side, chosen without leaving the menu (keys too). */
export function ThemeRow({
  theme,
  onThemeChange,
}: {
  theme: ThemePreference;
  onThemeChange: (theme: ThemePreference) => void;
}) {
  return (
    <div className="flex items-center gap-2 px-2 py-1.5 text-sm">
      <SunMoon className="size-4 text-muted-foreground" aria-hidden />
      <span className="flex-1">Theme</span>
      <MenuPrimitive.RadioGroup
        asChild
        value={theme}
        onValueChange={(value) => onThemeChange(value as ThemePreference)}
      >
        <fieldset aria-label="Theme" className="flex items-center rounded-md border p-0.5">
          {THEME_CHOICES.map(({ value, label, icon: Icon }) => (
            <Hint key={value} label={label}>
              {/* A radio item, so arrow keys reach it; choosing one keeps the menu open. */}
              <MenuPrimitive.RadioItem
                asChild
                value={value}
                onSelect={(event) => event.preventDefault()}
              >
                <button
                  type="button"
                  aria-label={label}
                  className={cn(
                    'flex size-7 items-center justify-center rounded text-muted-foreground outline-none hover:text-foreground focus-visible:text-foreground focus-visible:ring-1 focus-visible:ring-ring',
                    theme === value && 'bg-muted text-foreground',
                  )}
                >
                  <Icon className="size-4" />
                </button>
              </MenuPrimitive.RadioItem>
            </Hint>
          ))}
        </fieldset>
      </MenuPrimitive.RadioGroup>
    </div>
  );
}
